
import React, { useState, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ZoneSettings, ServiceZone } from '@/types';
import { toast } from 'sonner';
import { Edit } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdminAuth } from '@/hooks/useAdminAuth';

const ZonesList: React.FC = () => {
  const [zones, setZones] = useState<ZoneSettings[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { logAdminActivity } = useAdminAuth();
  
  useEffect(() => {
    const fetchZones = async () => {
      try {
        setIsLoading(true);
        
        // Try to fetch from the new service_zones table
        const { data: dbZones, error } = await supabase
          .from('service_zones')
          .select('*')
          .order('zone_name');

        if (error) {
          console.warn('Could not fetch zones from database, using mock data:', error);
          // Fallback to mock data
          setTimeout(() => {
            const mockZones: ZoneSettings[] = [
              {
                id: '1',
                zone: ServiceZone.PalmSprings,
                availableDays: [1, 3, 5],
                isActive: true,
                zipCodes: ['92262', '92264'],
                color: '#1B4D4B'
              },
              {
                id: '2',
                zone: ServiceZone.CathedralCity,
                availableDays: [2, 4],
                isActive: true,
                zipCodes: ['92234'],
                color: '#F4854B'
              },
              {
                id: '3',
                zone: ServiceZone.Other,
                availableDays: [5],
                isActive: false,
                zipCodes: ['92240', '92211'],
                color: '#7C8B6F'
              }
            ];
            
            setZones(mockZones);
            setIsLoading(false);
          }, 500);
          return;
        }

        if (dbZones && dbZones.length > 0) {
          // Convert database zones to ZoneSettings format
          const convertedZones: ZoneSettings[] = dbZones.map(zone => ({
            id: zone.id.toString(),
            zone: zone.zone_name as ServiceZone,
            availableDays: zone.available_days || [],
            isActive: zone.is_active || false,
            zipCodes: zone.zip_codes || [],
            color: zone.color || '#1B4D4B'
          }));
          
          setZones(convertedZones);
          logAdminActivity('zones_list_viewed', { count: convertedZones.length });
        } else {
          // No zones in database, use mock data
          const mockZones: ZoneSettings[] = [
            {
              id: '1',
              zone: ServiceZone.PalmSprings,
              availableDays: [1, 3, 5],
              isActive: true,
              zipCodes: ['92262', '92264'],
              color: '#1B4D4B'
            },
            {
              id: '2',
              zone: ServiceZone.CathedralCity,
              availableDays: [2, 4],
              isActive: true,
              zipCodes: ['92234'],
              color: '#F4854B'
            },
            {
              id: '3',
              zone: ServiceZone.Other,
              availableDays: [5],
              isActive: false,
              zipCodes: ['92240', '92211'],
              color: '#7C8B6F'
            }
          ];
          
          setZones(mockZones);
        }
      } catch (error) {
        console.error('Error fetching zones:', error);
        toast.error('Failed to load service zones');
        logAdminActivity('zones_list_error', { error: error.message });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchZones();
  }, [logAdminActivity]);
  
  const toggleZoneStatus = async (zoneId: string, currentStatus: boolean) => {
    try {
      // Try to update in database first
      const { error } = await supabase
        .from('service_zones')
        .update({ is_active: !currentStatus })
        .eq('id', parseInt(zoneId));
        
      if (error) {
        console.warn('Could not update zone in database, updating locally:', error);
      }
      
      // Update local state regardless
      setZones(prev => prev.map(zone => 
        zone.id === zoneId 
          ? { ...zone, isActive: !zone.isActive }
          : zone
      ));
      
      toast.success("Zone status updated", {
        description: "The service zone's status has been successfully updated."
      });

      logAdminActivity('zone_status_changed', { 
        zoneId, 
        newStatus: !currentStatus 
      });
    } catch (error) {
      console.error('Error updating zone:', error);
      toast.error('Failed to update zone status');
    }
  };
  
  const getDayNames = (days: number[]) => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days.map(day => dayNames[day]).join(', ');
  };
  
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }
  
  return (
    <div className="rounded-md border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Zone</TableHead>
            <TableHead className="hidden sm:table-cell">Available Days</TableHead>
            <TableHead className="hidden lg:table-cell">ZIP Codes</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {zones.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                No zones found
              </TableCell>
            </TableRow>
          ) : (
            zones.map((zone) => (
              <TableRow key={zone.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: zone.color }}
                    />
                    <span>{zone.zone}</span>
                  </div>
                </TableCell>
                <TableCell className="hidden sm:table-cell">
                  {getDayNames(zone.availableDays)}
                </TableCell>
                <TableCell className="hidden lg:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {zone.zipCodes.map((zipCode) => (
                      <Badge key={zipCode} variant="outline" className="text-xs">
                        {zipCode}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={zone.isActive}
                      onCheckedChange={() => toggleZoneStatus(zone.id, zone.isActive)}
                    />
                    <span className="text-sm">
                      {zone.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      toast("Edit zone", {
                        description: `Editing ${zone.zone} zone settings.`
                      });
                      logAdminActivity('zone_edit_initiated', { zoneId: zone.id });
                    }}
                  >
                    <Edit size={16} className="mr-1" />
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ZonesList;
